import { useFirebase, useFirestore } from "react-redux-firebase";
import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectMyUserId } from "../store/selectors";
import { clearCurrentUserId, setCurrentUserId } from "../store/actions";
import { FirebaseError } from "firebase/app";
import { userPath } from "../firestorePaths";

interface ReturnType {
  userId: string | undefined;
  login: (name: string, password: string) => void;
  logout: () => void;
  loginError: boolean;
  loginErrorMsg: string;
}

export const useUserId = (): ReturnType => {
  const firebase = useFirebase();
  const firestore = useFirestore();
  const dispatch = useDispatch();
  const currentUserId = useSelector(selectMyUserId);
  const [loginError, setLoginError] = useState(false);
  const [loginErrorMsg, setLoginErrorMsg] = useState("");
  const ensuredUserDocs = useRef<Set<string>>(new Set());

  // Ensure user document exists in Firestore when auth state changes
  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(async (authUser) => {
      if (authUser) {
        dispatch(setCurrentUserId(authUser.uid));
        
        // Ensure user document exists (handles case where auth exists but doc was deleted)
        if (!ensuredUserDocs.current.has(authUser.uid)) {
          ensuredUserDocs.current.add(authUser.uid);
          try {
            const path = userPath({ userId: authUser.uid });
            // Use set with merge to create if missing without overwriting existing data
            const name = authUser.email?.split('@')[0] || 'Player';
            await firestore.set(path, { name }, { merge: true });
          } catch (e) {
            console.error('Error ensuring user document:', e);
          }
        }
      } else {
        dispatch(clearCurrentUserId);
      }
    });
    return () => unsubscribe();
  }, [firebase, firestore, dispatch]);

  const login = async (name: string, password: string) => {
    console.log("login");
    setLoginError(false);
    setLoginErrorMsg("");
    const email = name + "@fake.com";
    try {
      await firebase.login({ email, password });
    } catch (e) {
      const { code } = e as FirebaseError;
      switch (code) {
        case "auth/wrong-password":
          setLoginError(true);
          setLoginErrorMsg(
            "That account exists, but that's not the password. Enter the correct password or a different name."
          );
          return;
      }

      try {
        await firebase.createUser({ email, password }, { name });
      } catch (e) {
        const { message } = e as FirebaseError;
        setLoginError(true);
        setLoginErrorMsg(message);
      }
    }
  };

  const logout = async () => {
    await firebase.logout();
  };

  return {
    userId: currentUserId,
    login,
    logout,
    loginError,
    loginErrorMsg,
  };
};
